/**
 * CrewPunchTable — compact punch table used by:
 *   • CrewLiveStatusCard  (crew-row caret expansion)
 *   • CrewMapCard         (expanded-modal site-list caret expansion)
 *
 * One row per CrewPunchSegment. Columns: Type chip | Job/Code | In | Out | Hrs.
 * Styles live in CrewWidgets.css (.cpt-*, .crew-punches).
 *
 * replaces PunchTimeline.tsx (vertical timeline) — timeline-event builder
 * (buildPunchTimeline) removed from crewUtil.ts.
 */

import React from 'react';
import { Clock, Coffee, LifeBuoy, Package, Wrench } from 'lucide-react';
import { CrewPunchSegment } from '../../interfaces/CrewLiveStatus';
import { formatPunchTime, isFreeTextSO, totalWorkedMinutes } from './crewUtil';

interface CrewPunchTableProps {
  punches: CrewPunchSegment[];
  /** 'in' | 'meal' | 'out' — needed for totalWorkedMinutes open-segment logic. */
  status: 'in' | 'meal' | 'out';
  nowMs: number;
  /** Role.KioskUser (TAS-541): uncoded raw-TE segments are hourly warehouse time, not piece. */
  kioskUser?: boolean;
}

/**
 * Type chip matching the Timesheet Review console's EntryTypeChip labels and
 * derivation (ETRC ~line 358): Meal → CSW → service order → Hourly (account-coded,
 * or NPM project-phase job) → uncoded raw punch by a kiosk role → Warehouse
 * (TAS-541) → uncoded Piece.
 */
const isWarehouseSeg = (seg: CrewPunchSegment, kioskUser?: boolean): boolean =>
  !!kioskUser && seg.punchSource === 'timeEntry' && !seg.projectName && !seg.accountCode;

const PunchTypeChip: React.FC<{ seg: CrewPunchSegment; kioskUser?: boolean }> = ({ seg, kioskUser }) => {
  if (seg.type === 2) {
    return (
      <span className="cpt-chip cpt-chip-meal">
        <Coffee />
        Meal
      </span>
    );
  }
  if (seg.customerServiceWarranty != null) {
    const isWarranty = seg.customerServiceWarranty.warrantyProject != null;
    return (
      <span className="cpt-chip cpt-chip-hourly">
        {isWarranty ? <Wrench /> : <LifeBuoy />}
        {isWarranty ? 'Warranty' : 'Cust Service'}
      </span>
    );
  }
  if (seg.serviceOrder) {
    return (
      <span className="cpt-chip cpt-chip-so">
        <Wrench />
        Service Order
      </span>
    );
  }
  if (seg.accountCode || seg.projectPhase) {
    return (
      <span className="cpt-chip cpt-chip-hourly">
        <Clock />
        Hourly
      </span>
    );
  }
  if (isWarehouseSeg(seg, kioskUser)) {
    return (
      <span className="cpt-chip cpt-chip-hourly">
        <Clock />
        Warehouse
      </span>
    );
  }
  return (
    <span className="cpt-chip cpt-chip-piece">
      <Package />
      Piece
    </span>
  );
};

/** Format decimal hours like fmtDec: (minutes / 60).toFixed(2) or "—" for zero. */
function fmtDec(minutes: number): string {
  if (!minutes) return '—';
  return (minutes / 60).toFixed(2);
}

/** Duration in minutes between two UTC timestamps, or from start to nowMs when out is null. */
function segmentMinutes(
  inDateTime: string,
  outDateTime: string | null,
  nowMs: number,
): number {
  const start = new Date(inDateTime).getTime();
  const end = outDateTime === null ? nowMs : new Date(outDateTime).getTime();
  return Math.max(0, Math.round((end - start) / 60000));
}

const CrewPunchTable: React.FC<CrewPunchTableProps> = ({ punches, status, nowMs, kioskUser }) => {
  const workedMin = totalWorkedMinutes(punches, status, nowMs);

  if (punches.length === 0) {
    return (
      <div className="cpt-wrap">
        <div className="cpt-head">
          <span>Today&rsquo;s punches</span>
        </div>
        <div className="cpt-empty">No punches recorded today.</div>
      </div>
    );
  }

  return (
    <div className="cpt-wrap">
      <div className="cpt-head">
        <span>Today&rsquo;s punches</span>
        <span className="cpt-total">{fmtDec(workedMin)} hrs total</span>
      </div>
      <div className="cpt-table-wrap">
        <table className="cpt-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Job / Code</th>
              <th>In</th>
              <th>Out</th>
              <th className="cpt-r">Hrs</th>
            </tr>
          </thead>
          <tbody>
            {punches.map((seg, idx) => {
              const isOpen = seg.outDateTime === null;
              const durMin = segmentMinutes(seg.inDateTime, seg.outDateTime, nowMs);

              /* Job / Code cell content */
              const csw = seg.customerServiceWarranty;
              // NPM free-text SO (no SO #): "Service Order" name + text sub-line
              const freeSO = !csw && isFreeTextSO(seg.serviceOrder);
              const jobName = csw
                ? (csw.locationName || csw.name || 'Service')
                : freeSO
                  ? 'Service Order'
                  : seg.projectName
                    || (seg.accountCode ? null : (isWarehouseSeg(seg, kioskUser) ? 'Warehouse' : 'Piece work'))
                    || '—';
              // CSW sub: "{name} · {description}" then optional warranty project line
              // Non-CSW sub: account code line, or NPM project-phase line
              const jobSub = csw
                ? (csw.name || csw.description
                    ? `${csw.name || ''}${csw.description ? ` · ${csw.description}` : ''}`
                    : null)
                : freeSO
                  ? seg.serviceOrder!.name
                  : seg.accountCode
                    ? `${seg.accountCode.altAccountCodeID} · ${seg.accountCode.name}`
                    : seg.projectPhase
                      ? `${seg.projectPhase.altProjectPhaseID} · ${seg.projectPhase.name}`
                      : null;
              const jobSub2 = csw?.warrantyProject
                ? `${csw.warrantyProject.name} · ${csw.warrantyProject.altProjectID}`
                : null;

              return (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={idx} className={isOpen ? 'cpt-open' : undefined}>
                  <td><PunchTypeChip seg={seg} kioskUser={kioskUser} /></td>
                  <td>
                    <div className="cpt-job-name">{jobName}</div>
                    {jobSub && <div className="cpt-job-sub">{jobSub}</div>}
                    {jobSub2 && <div className="cpt-job-sub">{jobSub2}</div>}
                  </td>
                  <td className="cpt-mono">{formatPunchTime(seg.inDateTime, seg.inDateTimeOffset)}</td>
                  <td className="cpt-mono">{isOpen ? '—' : formatPunchTime(seg.outDateTime!, seg.outDateTimeOffset)}</td>
                  <td className="cpt-mono cpt-r">{fmtDec(durMin)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CrewPunchTable;
